"use client";

import { useEffect, useRef } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Variants,
} from "framer-motion";
import { NavItem } from "./NavItem";
import type { SiteNavItem } from "./navigation.utils";
import styles from "./navigation.module.css";

type NavOverlayProps = {
  open: boolean;
  activeHref: string;
  items: SiteNavItem[];
  cta?: { label: string; href: string };
  eyebrow: string;
  closeLabel: string;
  ariaLabel: string;
  navLabel: string;
  returnFocusRef: React.RefObject<HTMLButtonElement>;
  onClose: () => void;
  onSelect: (event: React.MouseEvent<HTMLAnchorElement>, href: string) => void;
};

const panelVariants: Variants = {
  hidden: { opacity: 0, y: -72 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.48,
      ease: [0.22, 1, 0.36, 1] as const,
      when: "beforeChildren",
      staggerChildren: 0.06,
    },
  },
  exit: {
    opacity: 0,
    y: -48,
    transition: {
      duration: 0.28,
      ease: [0.4, 0, 1, 1] as const,
      when: "afterChildren",
      staggerChildren: 0.03,
      staggerDirection: -1,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: -14 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

export function NavOverlay({
  open,
  activeHref,
  items,
  cta,
  eyebrow,
  closeLabel,
  ariaLabel,
  navLabel,
  returnFocusRef,
  onClose,
  onSelect,
}: NavOverlayProps) {
  const firstLinkRef = useRef<HTMLAnchorElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = panelRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );

      if (!focusableElements || focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement as HTMLElement | null;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.setTimeout(() => {
      firstLinkRef.current?.focus();
    }, 40);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      returnFocusRef.current?.focus();
    };
  }, [open, onClose, returnFocusRef]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className={styles.overlay}
          initial={prefersReducedMotion ? false : { opacity: 0 }}
          animate={prefersReducedMotion ? undefined : { opacity: 1 }}
          exit={prefersReducedMotion ? undefined : { opacity: 0 }}
          transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          onClick={onClose}
        >
          <motion.div
            ref={panelRef}
            className={styles.panel}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            onClick={(event) => event.stopPropagation()}
            variants={prefersReducedMotion ? undefined : panelVariants}
            initial={prefersReducedMotion ? false : "hidden"}
            animate={prefersReducedMotion ? undefined : "visible"}
            exit={prefersReducedMotion ? undefined : "exit"}
          >
            <div className={styles.panelInner}>
              <div className={styles.panelTop}>
                <p className={styles.eyebrow}>{eyebrow}</p>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={onClose}
                >
                  {closeLabel}
                </button>
              </div>

              <motion.nav className={styles.menu} aria-label={navLabel}>
                {items.map((item, index) => (
                  <motion.div
                    key={item.href}
                    variants={prefersReducedMotion ? undefined : itemVariants}
                  >
                    <NavItem
                      ref={index === 0 ? firstLinkRef : undefined}
                      item={item}
                      active={item.href === activeHref}
                      onSelect={onSelect}
                    />
                  </motion.div>
                ))}
              </motion.nav>

              {cta ? (
                <motion.div
                  className={styles.footer}
                  variants={prefersReducedMotion ? undefined : itemVariants}
                >
                  <a
                    className={styles.ctaLink}
                    href={cta.href}
                    onClick={(event) => onSelect(event, cta.href)}
                  >
                    {cta.label}
                  </a>
                </motion.div>
              ) : null}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
